import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaArrowUp,
  FaArrowRight,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
} from "react-icons/fa";
import "./Footer.css";

const branches = [
  { to: "/campuses/boys", name: "Boys Branch", place: "Chowk Shah Abbas" },
  { to: "/campuses/girls", name: "Girls Branch", place: "Near Al-Jilan School" },
  { to: "/campuses/rangilpur", name: "Rangilpur Branch", place: "" },
];

const academics = [
  { to: "/academics/primary", label: "Primary School" },
  { to: "/academics/middle", label: "Middle School" },
  { to: "/academics/high", label: "High School" },
];

const quickLinks = [
  { to: "/about", label: "About Us" },
  { to: "/gallery", label: "Gallery" },
  { to: "/admissions", label: "Admissions" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

const socials = [
  { label: "YouTube", href: "https://www.youtube.com/@Academicgroupofschools", Icon: FaYoutube },
  { label: "Facebook", href: "https://facebook.com", Icon: FaFacebookF },
  { label: "Instagram", href: "https://instagram.com", Icon: FaInstagram },
  { label: "LinkedIn", href: "https://linkedin.com", Icon: FaLinkedinIn },
];

/* Developer credit (bottom bar) */
const DEV_NAME = "Farrukh Saleem";
const DEV_PHONE = "+92 319 7303243";
const DEV_WHATSAPP =
  "https://wa.me/923197303243?text=" +
  encodeURIComponent(
    "Assalam o Alaikum Farrukh, I saw your work on the Academic Group of Schools website and would like to talk about a project."
  );

/* ---------- Phone detection (matches the CSS breakpoint) ---------- */
const MOBILE_QUERY = "(max-width: 639px)";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange); // older Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

/* ---------- A footer link group: plain column on tablet/desktop,
   tap-to-open section on phones ---------- */
function FooterGroup({ id, title, className = "", isMobile, children }) {
  const [open, setOpen] = useState(false);
  const expanded = !isMobile || open;

  return (
    <nav
      className={`ft-col ${className}${isMobile ? " is-accordion" : ""}${expanded ? " is-open" : ""}`}
      aria-labelledby={`${id}-title`}
    >
      <h3 className="ft-title" id={`${id}-title`}>
        {isMobile ? (
          <button
            type="button"
            className="ft-acc-btn"
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            onClick={() => setOpen((o) => !o)}
          >
            <span>{title}</span>
            <span className="ft-acc-icon" aria-hidden="true" />
          </button>
        ) : (
          title
        )}
      </h3>
      <div className="ft-panel" id={`${id}-panel`}>
        <div className="ft-panel-inner">{children}</div>
      </div>
    </nav>
  );
}

function Footer() {
  const isMobile = useIsMobile();
  const [showTop, setShowTop] = useState(false);

  // Show the back-to-top button only after the visitor has scrolled a bit
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <footer className="ft">
        <div className="ft-container">
          {/* ---------- Admissions call-to-action ---------- */}
          <section className="ft-cta" aria-labelledby="ft-cta-title">
            <div className="ft-cta-text">
              <h2 id="ft-cta-title">Admissions Open</h2>
              <p>Give your child the right start. Join the Academic Group of Schools &amp; College family today.</p>
            </div>
            <div className="ft-cta-actions">
              <Link to="/admissions" className="ft-btn ft-btn--dark">
                Apply Now <FaArrowRight aria-hidden="true" />
              </Link>
              <Link to="/contact" className="ft-btn ft-btn--line">
                Contact Us
              </Link>
            </div>
          </section>

          {/* ---------- Main columns ---------- */}
          <div className="ft-main">
            <div className="ft-grid">
              {/* Brand */}
              <div className="ft-brand">
                <div className="ft-brand-head">
                  <img
                    className="ft-logo"
                    src="/images/logo.webp"
                    alt="Academic Group of Schools & College logo"
                  />
                  <div className="ft-brand-text">
                    <h2 className="ft-brand-name">
                      <span className="ft-brand-line1">Academic Group</span>
                      <span className="ft-brand-line2">of Schools &amp; College</span>
                           <span>Since 2005</span>
                    </h2>
                    
                  </div>
                </div>

                <p className="ft-desc">
                  Nurturing curious minds and strong character through quality education, one student at a time.
                </p>

                <div className="ft-social">
                  <p className="ft-social-label">Follow us</p>
                  <div className="ft-social-list">
                    {socials.map(({ label, href, Icon }) => (
                      <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                        <Icon aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Academics */}
              <FooterGroup id="ft-academics" title="Academics" className="ft-col--academics" isMobile={isMobile}>
                <ul className="ft-list">
                  {academics.map(({ to, label }) => (
                    <li key={to}>
                      <Link to={to}>{label}</Link>
                    </li>
                  ))}
                </ul>
              </FooterGroup>

              {/* Quick links */}
              <FooterGroup id="ft-links" title="Quick Links" className="ft-col--links" isMobile={isMobile}>
                <ul className="ft-list">
                  {quickLinks.map(({ to, label }) => (
                    <li key={to}>
                      <Link to={to}>{label}</Link>
                    </li>
                  ))}
                </ul>
              </FooterGroup>

              {/* Branches */}
              <FooterGroup id="ft-branches" title="Our Branches" className="ft-col--branches" isMobile={isMobile}>
                <ul className="ft-list ft-list--branches">
                  {branches.map(({ to, name, place }) => (
                    <li key={to}>
                      <Link to={to}>
                        <span>
                          <strong>{name}</strong>
                          {place && <small>{place}</small>}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </FooterGroup>

              {/* Contact (always open) */}
              <div className="ft-col ft-col--contact">
                <h3 className="ft-title">Reach out to us</h3>
                <address className="ft-contact">
                  <a
                    className="ft-contact-item"
                    href="https://www.google.com/maps/search/?api=1&query=Chowk+Shah+Abbas+Suraj+Kund+Road+Multan"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="ft-contact-icon">
                      <FaMapMarkerAlt aria-hidden="true" />
                    </span>
                    <span className="ft-contact-text">
                      <small>Visit us</small>
                      Chowk Shah Abbas, Suraj Kund Road, Multan
                    </span>
                  </a>

                  <a className="ft-contact-item" href="mailto:academicgroupofschools@gmail.com">
                    <span className="ft-contact-icon">
                      <FaEnvelope aria-hidden="true" />
                    </span>
                    <span className="ft-contact-text">
                      <small>Email us</small>
                      academicgroupofschools<wbr />@gmail.com
                    </span>
                  </a>

                  <a className="ft-contact-item" href="tel:+923047630462">
                    <span className="ft-contact-icon">
                      <FaPhoneAlt aria-hidden="true" />
                    </span>
                    <span className="ft-contact-text">
                      <small>Call us</small>
                      0304 7630462
                    </span>
                  </a>
                </address>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Bottom bar ---------- */}
        <div className="ft-bottom">
          <div className="ft-container ft-bottom-inner">
            <p className="ft-copy">
              © {new Date().getFullYear()} Academic Group of Schools &amp; College. All rights reserved.
            </p>

            <div className="ft-credit">
              <span className="ft-credit-text">
                Designed &amp; Developed by <strong>{DEV_NAME}</strong>
              </span>
              <a
                className="ft-credit-contact"
                href={DEV_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Contact ${DEV_NAME} on WhatsApp at ${DEV_PHONE}`}
              >
                <FaWhatsapp aria-hidden="true" />
                <span>{DEV_PHONE}</span>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        type="button"
        className={`ft-backtop${showTop ? " is-visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <FaArrowUp aria-hidden="true" />
      </button>
    </>
  );
}

export default Footer;